"use client";

import {
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@softmax/design-system";
import {
  ResourceFeedback,
  type ChartState,
} from "@softmax/web-ux/resource-feedback";
import * as stylex from "@stylexjs/stylex";
import type { CreatorStatus } from "./coworld-records";
import { coworldLayout } from "./coworld-layout.styles";
import { CoworldStatus } from "./coworld-status";

export function CoworldRuns({
  state,
  onRefresh,
  hasMore,
  loadingMore,
  onMore,
  newRunHref,
}: Readonly<{
  state: ChartState<
    Readonly<{
      runs: readonly Readonly<{
        id: string;
        href: string;
        title: string;
        status: CreatorStatus;
        progress: string;
        created: string;
        createdAt: string;
      }>[];
    }>
  >;
  onRefresh(): void;
  hasMore: boolean;
  loadingMore: boolean;
  onMore(): void;
  newRunHref: string;
}>) {
  return (
    <div {...stylex.props(coworldLayout.stack)}>
      <div {...stylex.props(coworldLayout.row)}>
        <h2 {...stylex.props(coworldLayout.heading)}>Runs for this version</h2>
        <Button size="sm" variant="quiet" onClick={onRefresh}>
          ↻ Refresh runs
        </Button>
      </div>
      <ResourceFeedback state={state} subject="runs" onRefresh={onRefresh} />
      {state.status === "ready" &&
        (state.runs.length ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Run</TableHeaderCell>
                <TableHeaderCell>CoworldStatus</TableHeaderCell>
                <TableHeaderCell>
                  <span {...stylex.props(coworldLayout.secondaryColumn)}>
                    Games
                  </span>
                </TableHeaderCell>
                <TableHeaderCell align="end">
                  <span {...stylex.props(coworldLayout.secondaryColumn)}>
                    Started
                  </span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell>
                    <a href={run.href} {...stylex.props(coworldLayout.rowLink)}>
                      {run.title}
                    </a>
                    <span {...stylex.props(coworldLayout.mobile)}>
                      {run.progress} · {run.created}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div {...stylex.props(coworldLayout.cell)}>
                      <CoworldStatus {...run.status} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      {...stylex.props(
                        coworldLayout.cell,
                        coworldLayout.secondaryColumn,
                      )}
                    >
                      {run.progress}
                    </span>
                  </TableCell>
                  <TableCell align="end">
                    <time
                      {...stylex.props(
                        coworldLayout.cell,
                        coworldLayout.secondaryColumn,
                      )}
                      dateTime={run.createdAt}
                      title={new Date(run.createdAt).toLocaleString()}
                    >
                      {run.created}
                    </time>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div {...stylex.props(coworldLayout.empty)}>
            <h3 {...stylex.props(coworldLayout.heading)}>
              No runs for this version
            </h3>
            <p {...stylex.props(coworldLayout.muted)}>
              Run your Coworld with bots, then review the games and replays
              here.
            </p>
            <Link href={newRunHref} focus="native">
              Set up a private run →
            </Link>
          </div>
        ))}
      {hasMore && (
        <Button size="sm" disabled={loadingMore} onClick={onMore}>
          {loadingMore ? "Loading…" : "Load older runs"}
        </Button>
      )}
    </div>
  );
}
